import { cn } from "@design-system/lib/utils";
import { Button } from "@design-system/ui/button";
import { trans } from "@mongez/localization";
import { Moon, Sun } from "lucide-react";
import { useEffect, useState } from "react";
import { useDarkMode } from "../hooks/use-dark-mode.hook";

type DarkModeToggleProps = {
  variant?: "icon" | "button" | "switch";
  size?: "sm" | "md" | "lg" | "xs";
  className?: string;
};

export function DarkModeToggle({
  variant = "switch",
  size = "md",
  className,
}: DarkModeToggleProps) {
  const { isDark, toggleDarkMode } = useDarkMode();

  // State to track if user is on mobile
  const [isMobile, setIsMobile] = useState(false);

  // Effect to handle window resize and initial detection
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };

    // Initial check
    checkIsMobile();

    // Add resize listener
    window.addEventListener("resize", checkIsMobile);

    // Cleanup
    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  const getIcon = () => {
    return isDark ? Moon : Sun;
  };

  const getLabel = () => {
    return isDark ? trans("lightMode") : trans("darkMode");
  };

  const Icon = getIcon();

  // Switch variant (default) - responsive design
  if (variant === "switch") {
    // On mobile, show as button
    if (isMobile) {
      return (
        <Button
          aria-label={getLabel()}
          aria-checked={isDark}
          role="switch"
          variant="outline"
          size={size}
          type="button"
          onClick={toggleDarkMode}
          className={cn(
            "relative overflow-hidden transition-all duration-300 hover:scale-105",
            "hover:border-primary/30 hover:bg-primary/10 hover:text-primary",
            className,
          )}
        >
          <Icon
            className={cn(
              "transition-all duration-300",
              size === "sm" && "h-4 w-4",
              size === "md" && "h-4 w-4",
              size === "lg" && "h-5 w-5",
              size === "xs" && "h-3 w-3",
              // Sun icon (light theme) - orange color
              !isDark && "text-orange-500",
              // Moon icon (dark theme) - white color
              isDark && "text-white",
            )}
          />

          {/* Animated background effect */}
          <div className="absolute inset-0 rounded-md bg-gradient-to-r from-primary/10 to-secondary/10 opacity-0 transition-opacity duration-300 hover:opacity-100" />
        </Button>
      );
    }

    // On desktop, show as switch-like toggle
    return (
      <Button
        aria-label={getLabel()}
        aria-checked={isDark}
        role="switch"
        variant="ghost"
        size="icon"
        onClick={toggleDarkMode}
        className={cn(
          "relative overflow-hidden transition-all duration-300 hover:scale-105",
          "hover:bg-primary/10 hover:text-primary",
          "rounded-full border-2 transition-all duration-300",
          // Light mode styling
          !isDark &&
            "border-orange-200 bg-orange-50 hover:border-orange-300 hover:bg-orange-100",
          // Dark mode styling
          isDark &&
            "border-blue-200 bg-blue-50 hover:border-blue-300 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-900/20 dark:hover:border-blue-700 dark:hover:bg-blue-900/30",
          size === "sm" && "h-8 w-8",
          size === "md" && "h-9 w-9",
          size === "lg" && "h-10 w-10",
          className,
        )}
        title={getLabel()}
      >
        <Icon
          className={cn(
            "transition-all duration-300",
            size === "sm" && "h-4 w-4",
            size === "md" && "h-5 w-5",
            size === "lg" && "h-6 w-6",
            // Sun icon (light theme) - orange color
            !isDark && "text-orange-500",
            // Moon icon (dark theme) - blue color
            isDark && "text-blue-500 dark:text-blue-400",
          )}
        />

        {/* Animated background effect */}
        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-primary/20 to-secondary/20 opacity-0 transition-opacity duration-300 hover:opacity-100" />
      </Button>
    );
  }

  if (variant === "icon") {
    return (
      <Button
        aria-label={getLabel()}
        variant="ghost"
        size="icon"
        onClick={toggleDarkMode}
        className={cn(
          "relative overflow-hidden transition-all duration-300 hover:scale-105",
          "hover:bg-primary/10 hover:text-primary",
          size === "sm" && "h-8 w-8",
          size === "md" && "h-9 w-9",
          size === "lg" && "h-10 w-10",
          className,
        )}
        title={getLabel()}
      >
        <Icon
          className={cn(
            "transition-all duration-300",
            size === "sm" && "h-4 w-4",
            size === "md" && "h-5 w-5",
            size === "lg" && "h-6 w-6",
            // Sun icon (light theme) - orange color
            !isDark && "text-orange-500",
            // Moon icon (dark theme) - blue color
            isDark && "text-blue-500 dark:text-blue-400",
          )}
        />

        {/* Animated background effect */}
        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-primary/20 to-secondary/20 opacity-0 transition-opacity duration-300 hover:opacity-100" />
      </Button>
    );
  }

  if (variant === "button") {
    return (
      <Button
        aria-label={getLabel()}
        variant="outline"
        size={size}
        onClick={toggleDarkMode}
        className={cn(
          "relative overflow-hidden transition-all duration-300 hover:scale-105",
          "hover:border-primary/30 hover:bg-primary/10 hover:text-primary",
          className,
        )}
      >
        <Icon
          className={cn(
            "mr-2 transition-all duration-300",
            size === "sm" && "h-4 w-4",
            size === "md" && "h-4 w-4",
            size === "lg" && "h-5 w-5",
            // Sun icon (light theme) - orange color
            !isDark && "text-orange-500",
            // Moon icon (dark theme) - blue color
            isDark && "text-blue-500 dark:text-blue-400",
          )}
        />
        {getLabel()}

        {/* Animated background effect */}
        <div className="absolute inset-0 rounded-md bg-gradient-to-r from-primary/10 to-secondary/10 opacity-0 transition-opacity duration-300 hover:opacity-100" />
      </Button>
    );
  }

  return null;
}
